// Deterministic PRNG. Threading a seed through the whole game makes seasons
// reproducible — shareable "play my exact season" links, replays for animation,
// and (later) server-side verification of leaderboard scores by replaying the
// seed. Retrofitting this after the fact is painful; doing it now is free.
export class Rng {
  private state: number;

  constructor(seed: number) {
    // ensure a non-zero 32-bit state
    this.state = (seed >>> 0) || 0x9e3779b9;
  }

  // Draws taken so far. Persisted as SeasonState.rngDraws so a reloaded season
  // can fast-forward to the same position and continue the SAME sequence —
  // without it, the remaining races diverge from the canonical run.
  private count = 0;

  /** How many random numbers have been drawn from this generator. */
  get draws(): number {
    return this.count;
  }

  /** Advance by `n` draws, discarding the values — restores a saved position. */
  skip(n: number): void {
    for (let i = 0; i < n; i++) this.next();
  }

  /** float in [0, 1) */
  next(): number {
    this.count++;
    let t = (this.state += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** integer in [min, max] inclusive */
  int(min: number, max: number): number {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }

  /** pick a random element; throws on empty input so bugs surface loudly */
  pick<T>(arr: readonly T[]): T {
    if (arr.length === 0) throw new Error("Rng.pick: empty array");
    return arr[this.int(0, arr.length - 1)]!;
  }

  /** Fisher–Yates shuffle into a new array */
  shuffle<T>(arr: readonly T[]): T[] {
    const out = arr.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.int(0, i);
      [out[i], out[j]] = [out[j]!, out[i]!];
    }
    return out;
  }
}

/** A reasonable random seed for a fresh, non-shared game. */
export function randomSeed(): number {
  return (Math.random() * 0xffffffff) >>> 0;
}

/** Stable 32-bit seed derived from a YYYY-MM-DD day key (FNV-1a hash). Pinning
 * the seed to the date is what makes the Daily Challenge identical for everyone. */
export function seedFromDate(day: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < day.length; i++) {
    h ^= day.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Combine a base seed with an integer into a fresh, well-mixed 32-bit seed.
 * Used to spin up an independent per-race RNG (seed + round) so race luck is a
 * pure function of the date — never shifted by draft rerolls or reloads. */
export function mixSeed(seed: number, n: number): number {
  let h = (seed ^ Math.imul(n + 1, 0x9e3779b9)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return (h ^ (h >>> 16)) >>> 0;
}
